import { useEffect, useState } from "react";
import { Input } from "antd";
import { useLoader } from "../../../../../utils/LoaderContext";
import {
  useAllOrders,
  useSearchOrders,
} from "../../../../../api/orders/orders";

const ORDERS_PER_PAGE = 20;

const Cancelled = ({ status = "cancelled" }) => {
  const { setLoading } = useLoader();
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Search API (used when search is active)
  const { data: searchedOrders = [], isLoading: searching } = useSearchOrders(
    debouncedSearchText,
    status
  );

  // Paginated orders API (for default listing)
  const {
    data: allOrders = { orders: [], totalPages: 1 },
    isLoading: loadingAll,
    isFetching,

    refetch,
  } = useAllOrders({ currentPage, ORDERS_PER_PAGE, status });

  useEffect(() => {
    setLoading(true);
    refetch().finally(() => setLoading(false));
  }, [refetch, setLoading]);

  const orders = debouncedSearchText ? searchedOrders : allOrders.orders || [];
  const isLoading = debouncedSearchText ? searching : loadingAll;
  const totalPages = debouncedSearchText ? 1 : allOrders.totalPages || 1;

  return (
    <div className="w-full mt-6">
      {/* Search Input */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1000px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">Order Id / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer Name / Contact</th>
              <th className="p-3">Address</th>
              <th className="p-3">courier details</th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={6}>
                  <div className="flex justify-center items-center py-10 text-gray-500 space-x-2">
                    <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                    <span>Loading cancelled orders...</span>
                  </div>
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.order_number}
                    </div>
                    <div className="text-xs font-semibold px-2 py-1 rounded inline-block mt-1 bg-red-100 text-red-600">
                      {order.shippingStatus}
                    </div>
                  </td>
                  <td className="p-4">
                    <p>₹{order.total_amount}</p>
                    <span className="inline-block text-xs bg-green-100 text-green-700 px-2 py-1 rounded mt-1">
                      {order.PaymentType}
                    </span>
                  </td>
                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.name}
                      </p>
                    ))}
                  </td>
                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.phone}</p>
                  </td>
                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.city}</p>
                    <p>{order.BillingAddress?.state}</p>
                    <p>{order.BillingAddress?.pincode}</p>
                  </td>
                  <td className="p-4">
                    <p className="font-medium">{order?.courier || "N/A"}</p>
                    {order.awbNumber ? (
                      <a
                        href={`https://shipclues.com/track-order/${order.awbNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors duration-200 mt-1"
                      >
                        Track Order
                      </a>
                    ) : (
                      <span className="inline-block px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded mt-1">
                        AWB Not Available
                      </span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center py-6 text-gray-400">
                  No cancelled orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!debouncedSearchText && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
            {/* {isFetching && (
              <span className="ml-3 text-blue-500 animate-pulse">
                Loading...
              </span>
            )} */}
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || isFetching}
              className={`px-4 py-2 rounded border ${
                currentPage === 1 || isFetching
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || isFetching}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages || isFetching
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cancelled;
